import { useState, useRef, useEffect } from 'react'
import { Moon, Sun, HelpCircle, Menu, ChevronDown, LogOut, Shield, ShieldOff } from 'lucide-react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import {
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useTheme } from '@/hooks/useTheme'
import { useAppContextOptional } from '@/hooks/useAppContext'
import { useCanAccessAdmin } from '@/hooks/useCanAccessAdmin'
import { useSuperadminMode } from '@/hooks/useSuperadminMode'
import { setSuperadminMode, clearSuperadminMode } from '@/auth/superadminMode'
import { authClient } from '@/auth/authClient'
import { OrgWorkspacePicker } from './OrgWorkspacePicker'
import { HelpPanel } from '@/help/HelpPanel'
import { NotificationsBell } from '@/components/NotificationsBell'

interface HeaderProps {
  onMenuClick: () => void
}

/**
 * When the dropdown switches the variant, decide whether the URL
 * also needs to move. The dashboard reads `variantId` from URL
 * params (so deep-links share a clean address), so a pure context
 * update wouldn't re-render the dashboard view. Other pages
 * (TestRunner, etc.) read from context state and refresh
 * automatically, so they don't need the redirect.
 *
 * The workspace id is derived from `currentPath` rather than taken
 * as a separate argument — during a workspace transition the
 * context's `workspaceId` and the URL's wid can briefly disagree,
 * and the redirect must stay on the workspace the user is *visibly*
 * on, not the one context is about to settle on.
 *
 * `currentSearch` is preserved through the redirect (minus `run`,
 * which is a per-run selection that doesn't survive a variant
 * switch). The dashboard's filter state lives in `window` /
 * `runType` query params — losing those on every dropdown switch
 * was the second half of the "variant switch doesn't refresh"
 * symptom.
 *
 * Returns the new path to navigate to, or `null` to leave the URL
 * alone. Exported for direct unit testing.
 */
export function nextPathOnVariantChange(
  currentPath: string,
  currentSearch: string,
  newVariantId: string,
): string | null {
  // Only intervene when we're already on a variant-scoped page.
  const m = currentPath.match(/^\/workspaces\/([^/]+)\/variants\//)
  if (!m) return null
  const wid = m[1]

  // Preserve search params (e.g. `window`, `runType`) so dropdown
  // switches don't reset the user's dashboard filters. Drop `run`
  // because that runId belonged to the old variant — keeping it
  // would deep-link to a meaningless cell on the new dashboard.
  const params = new URLSearchParams(currentSearch)
  params.delete('run')
  const search = params.toString() ? `?${params.toString()}` : ''

  // Picking a different variant takes the user to that variant's
  // dashboard. We deliberately don't preserve trailing segments
  // like `/runs/:rid` — that runId belongs to the OLD variant.
  if (newVariantId) {
    return `/workspaces/${wid}/variants/${newVariantId}/dashboard${search}`
  }
  // App-only selection (no variant) — bounce up to /reports.
  // ReportsRedirect there will navigate to the active variant's
  // dashboard when `selectedVariantId` is set, or render the
  // "select a variant" prompt when it isn't (since we just
  // cleared it in handleAppVariantChange, the prompt is what the
  // user will see — the right next step to nudge them to pick one).
  return `/workspaces/${wid}/reports${search}`
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, setTheme } = useTheme()
  // Workspace context is optional — present under /workspaces/:wid/*
  // routes, absent under identity-only routes like /admin/* (#429 Wave
  // 1 Track C). When absent, the workspace-scoped controls (org/ws
  // picker, app/variant dropdown, "set up application" prompt) are
  // hidden; the identity-only controls on the right (theme, account,
  // help, sign out) keep working in both modes.
  const appCtx = useAppContextOptional()
  const workspaceId = appCtx?.workspaceId ?? ''
  const apps = appCtx?.apps ?? []
  const allVariants = appCtx?.allVariants ?? []
  const selectedAppId = appCtx?.selectedAppId ?? ''
  const selectedVariantId = appCtx?.selectedVariantId ?? ''
  const setSelectedAppId = appCtx?.setSelectedAppId
  const setSelectedVariantId = appCtx?.setSelectedVariantId

  // Superadmin-mode toggle (#429 Wave 2). Only the persisted flag
  // gates the toggle's visibility — the toggle itself is the
  // per-session opt-in (NIST AC-6(2)). Non-superadmins never see it.
  const canAccessAdmin = useCanAccessAdmin()
  const superadminMode = useSuperadminMode()

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  // Sign-out confirmation dialog state. Click the logout icon →
  // opens the dialog; click Confirm → actually signs out. Cancel
  // closes the dialog and the session is preserved. The intent of
  // the gate is to catch accidental clicks (the icon sits next to
  // the help icon and is a single tap).
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  // Top-nav Help panel. Default landing topic is the verb-first
  // 'how-to' hub at apps/docs/src/content/docs/how-to.mdx (PR #926) — the natural
  // 'I need help, don't know where' surface. Click → drawer; the
  // drawer renders title + summary + a 'read full docs →' link to
  // the corresponding page on docs.ottotester.ai. Closes #930.
  const [helpOpen, setHelpOpen] = useState(false)

  /** Better Auth handles cookie clear server-side; the redirect to
   * /auth/login resets the in-memory identity context as App.tsx's
   * gate sees the unauthenticated state and routes accordingly.
   * `replace: true` so the user can't accidentally back-button into
   * a stale authenticated page (its API calls would 401, but the
   * URL would still flicker). */
  const handleSignOut = async () => {
    setSigningOut(true)
    // Drop the superadmin-mode toggle BEFORE the network call so any
    // in-flight request that races the signOut doesn't carry a stale
    // escalation header. The toggle is per-session by design (#429
    // Wave 2) — explicit clear here, sessionStorage clears on tab
    // close, and the toggle UI is hidden for non-superadmins after
    // signOut so the next session always lands with it OFF.
    clearSuperadminMode()
    try {
      await authClient.signOut()
    } catch {
      // Even if signOut throws (network blip), navigate away —
      // the cookie may already be cleared, or the session is dead
      // anyway. The login screen will surface a real auth error if
      // the user can't get back in.
    }
    navigate('/auth/login', { replace: true })
  }

  // Build combined app+variant options using all variants
  const appVariantOptions = apps.flatMap((app) => {
    const appVariants = allVariants.filter((v) => v.applicationId === app.id)
    if (appVariants.length === 0) {
      return [{
        value: `${app.id}:`,
        appName: app.name,
        variantName: '',
        status: '' as any,
        appId: app.id,
        variantId: '',
      }]
    }
    return appVariants.map((v) => ({
      value: `${app.id}:${v.id}`,
      appName: app.name,
      variantName: v.name,
      status: v.status,
      appId: app.id,
      variantId: v.id,
    }))
  })

  const currentValue = `${selectedAppId}:${selectedVariantId}`
  const currentOption = appVariantOptions.find(o => o.value === currentValue)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const handleAppVariantChange = (value: string) => {
    // Unreachable without workspace context — the dropdown that fires
    // this handler is only rendered when `apps.length > 0`, which is
    // only true when `appCtx` is non-null. Guarded defensively because
    // the setters are typed as possibly-undefined.
    if (!setSelectedAppId || !setSelectedVariantId) return
    const [appId, variantId] = value.split(':')
    setSelectedAppId(appId)
    setSelectedVariantId(variantId || '')
    setDropdownOpen(false)
    // If the user is currently looking at a variant-scoped page,
    // also move the URL — VariantDashboard renders against the URL
    // param, not context state, so a pure context update would
    // leave the view stuck on the old variant. The helper derives
    // the workspace id from `location.pathname` to avoid using a
    // mid-transition stale `workspaceId` from context.
    const next = nextPathOnVariantChange(
      location.pathname,
      location.search,
      variantId || '',
    )
    if (next) navigate(next)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 border-b bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/60">
      <div className="flex h-full items-center justify-between px-4">
        {/* Left side: Logo + App Selector.
            `min-w-0` is load-bearing (#1822): a flex child defaults to
            `min-width: auto`, so without it this cluster refuses to shrink
            below its own content and pushes the fixed header — and with it the
            whole page — wider than the viewport. Measured at 375px before the
            fix: this cluster 445px, the row's right edge at 657px. The row is
            `justify-between`, so this is the side that must yield; the right
            cluster is fixed-size icon buttons with nothing to give. */}
        <div data-testid="header-left-cluster" className="flex items-center gap-3 min-w-0 flex-1">
          <Button variant="ghost" size="icon-sm" onClick={onMenuClick} className="lg:hidden">
            <Menu className="h-5 w-5" />
          </Button>

          {/* Logo — separate light/dark variants so the wordmark
              has correct contrast on both themes. Tailwind's
              `dark:` variant flips visibility based on the
              `dark` class applied to <html> by useTheme.
              Wrapped in <Link to="/"> so clicking it always takes
              the user home: from workspace routes it stays on the
              active workspace's runner via WorkspaceRedirect; from
              /admin/* (no workspace context) it also lands on the
              workspace runner via the same redirect — the
              IdentityGate → WithWorkspaceContext → WorkspaceRedirect
              chain handles both modes. */}
          <Link
            to="/"
            aria-label="OttoTester home"
            className="flex items-center focus:outline-none focus:ring-2 focus:ring-ring rounded"
          >
            <img
              src="/brand/wordmark-light.png"
              alt="OttoTester"
              className="h-7 w-auto block dark:hidden"
            />
            <img
              src="/brand/wordmark-dark.png"
              alt="OttoTester"
              className="h-7 w-auto hidden dark:block"
            />
          </Link>

          {/* Workspace switcher — compact `header-tile` pill (28×28
              colored tile + workspace name + chevron) sitting to the
              LEFT of the app/variant dropdown. Workspace-scoped:
              hidden when there's no AppContext (admin-only routes).
              Placement chosen so switching workspace is one click
              from any page without dominating the icon-stacked
              sidebar. A prior iteration mounted this in the sidebar
              above the nav stack — user feedback flagged it as too
              prominent for the nav. */}
          {workspaceId && (
            <OrgWorkspacePicker
              activeWorkspaceId={workspaceId}
              variant="header-tile"
            />
          )}

          {/* App/Variant Selector — workspace-scoped. Without context
              apps is empty AND we shouldn't render the empty-state
              CTA either (which would link into the workspace). The
              outer `appCtx &&` guard handles both branches together. */}
          {appCtx && apps.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="app-variant-selector"
                onClick={() => setDropdownOpen(prev => !prev)}
                // #1822 — `min-w-0` lets the button shrink; the label spans
                // truncate inside it. The full value is never lost: it is in
                // the `title` below and in the dropdown this opens.
                className="flex items-center gap-2 h-8 px-3 text-sm font-medium bg-muted rounded-md cursor-pointer hover:bg-muted/80 focus:outline-hidden focus:ring-2 focus:ring-ring min-w-0"
                title={
                  currentOption
                    ? [currentOption.appName, currentOption.variantName, currentOption.status]
                        .filter(Boolean)
                        .join(' · ')
                    : undefined
                }
              >
                {currentOption ? (
                  <>
                    <span className="truncate">{currentOption.appName}</span>
                    {currentOption.variantName && (
                      <>
                        <span className="text-muted-foreground shrink-0">·</span>
                        <span className="truncate">{currentOption.variantName}</span>
                      </>
                    )}
                    {currentOption.status && (
                      // The status pill is short and carries the most meaning
                      // per pixel, so it keeps its size while the names give.
                      <Badge variant={currentOption.status as any} className="text-[10px] px-1.5 py-0 shrink-0">{currentOption.status}</Badge>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground whitespace-nowrap">Select app…</span>
                )}
                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-popover border rounded-lg shadow-lg z-50 py-1 max-h-80 overflow-y-auto">
                  {appVariantOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAppVariantChange(opt.value)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${opt.value === currentValue ? 'bg-primary/10' : ''}`}
                    >
                      <span className="font-medium">{opt.appName}</span>
                      {opt.variantName && (
                        <>
                          <span className="text-muted-foreground">·</span>
                          <span>{opt.variantName}</span>
                        </>
                      )}
                      {opt.status && (
                        <Badge variant={opt.status as any} className="text-[10px] px-1.5 py-0 ml-auto">{opt.status}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {appCtx && apps.length === 0 && (
            <button
              onClick={() => navigate(`/workspaces/${workspaceId}/applications`)}
              className="ml-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Set up an application to start
            </button>
          )}
        </div>

        {/* Right side */}
        {/* Right side. `shrink-0` so the icon cluster keeps its size and the
            left side absorbs the squeeze instead — compressing icon buttons
            would make them unreadable rather than merely abbreviated. */}
        <div data-testid="header-right-cluster" className="flex items-center gap-1 shrink-0">
          {/* Superadmin-mode toggle (#429 Wave 2). Visible only to
              users with the persisted `isSuperadmin` flag — for
              everyone else this entire affordance is absent so the
              header looks identical to a non-superadmin's view. The
              icon flips Shield (off) ↔ ShieldOff (on) — Shield reads
              as "armed" when off (the default safe state), ShieldOff
              reads as "open" when escalation is active. Aria-pressed
              + a deliberate red ring when ON make the active state
              obvious in case the icon swap is missed at a glance. */}
          {canAccessAdmin && (
            <Button
              variant="ghost"
              size="icon"
              aria-pressed={superadminMode}
              onClick={() => setSuperadminMode(!superadminMode)}
              title={superadminMode ? 'Turn off Global Admin mode' : 'Turn on Global Admin mode'}
              className={superadminMode ? 'ring-2 ring-destructive text-destructive' : ''}
            >
              {superadminMode ? (
                <ShieldOff className="h-5 w-5" />
              ) : (
                <Shield className="h-5 w-5" />
              )}
            </Button>
          )}
          <NotificationsBell />
          <Button variant="ghost" size="icon" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
          {/* No account icon in the header. PR 6 briefly added a
              UserCircle here but it landed on the same /settings
              page the sidebar Settings entry already opens — just
              on a different default tab. Users reach Account via
              sidebar Settings → click "Account" in the section
              rail (one extra click for the no-more-duplicate-
              destinations win). */}
          {/* Top-nav Help — the global entry point. Opens the in-app
              HelpPanel side drawer with the verb-first 'how-to' hub
              as the default landing topic. From the drawer the user
              can follow 'read full docs →' to the corresponding page
              on docs.ottotester.ai. Closes #930. */}
          <Button
            variant="ghost"
            size="icon"
            title="Help"
            aria-label="Open help"
            onClick={() => setHelpOpen(true)}
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSignOutConfirmOpen(true)}
            title="Sign out"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <Dialog
        open={signOutConfirmOpen}
        onOpenChange={(o) => {
          // Block close-while-signing-out so the user can't
          // dismiss the dialog mid-flight and confuse themselves
          // about whether the action ran.
          if (!signingOut) setSignOutConfirmOpen(o)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign out?</DialogTitle>
            <DialogDescription>
              You'll be signed out of this browser. Any in-flight test
              runs continue server-side; you can sign back in any time
              to see their results.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSignOutConfirmOpen(false)}
              disabled={signingOut}
            >
              Cancel
            </Button>
            <Button onClick={handleSignOut} disabled={signingOut}>
              {signingOut ? 'Signing out…' : 'Yes, sign out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <HelpPanel slug="how-to" open={helpOpen} onOpenChange={setHelpOpen} />
    </header>
  )
}
